// src/screens/admin/customerDetail/CustomerDetail.jsx

import { useState, useEffect, useContext } from "react";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ShoppingBag,
  Package,
  DollarSign,
  TrendingUp,
  Clock,
  User,
  Hash,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import "../../../assets/css/admin/customers/CustomerDetail.css";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    maximumFractionDigits: 0,
  }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-PK", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

const AVATAR_COLORS = [
  "#D67E43", "#5C6B47", "#C96F38", "#4A5838",
  "#B85C2A", "#7A8F5F", "#E8A06A", "#6B4C3B",
];

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
};

// ─────────────────────────────────────────────────────────────
// ORDER STATUS BADGE
// ─────────────────────────────────────────────────────────────
const OrderBadge = ({ status }) => (
  <span className={`cd-order-badge ${status?.toLowerCase()}`}>
    {status || "—"}
  </span>
);

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const CustomerDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { baseUrl, authToken } = useContext(AppContext);

  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalSpent: 0,
    averageOrder: 0,
    delivered: 0,
  });

  // ── Fetch customer detail ──
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/auth/viewCustomerDetail/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setCustomer(res.data.customer);
        setOrders(res.data.orders || []);
        setStats(res.data.stats);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load customer details. Please try again.");
        setLoading(false);
      });
  }, [id]);

  // ── Derived stats ──
  // const totalSpent = orders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
  // const totalOrders = orders.length;
  // const deliveredCount = orders.filter((o) => o.status?.toLowerCase() === "delivered").length;
  // const avgOrderValue = totalOrders > 0 ? totalSpent / totalOrders : 0;

  const avatarColor = getAvatarColor(customer?.fullName || customer?.name);

  // ─────────────────────────────────────────────────────────────
  // LOADING
  // ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="cd-container">
        <div className="cd-loading">
          <div className="cd-spinner" />
          <span className="cd-loading-text">Loading customer details…</span>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // ERROR
  // ─────────────────────────────────────────────────────────────
  if (error || !customer) {
    return (
      <div className="cd-container">
        <div className="cd-error">
          <span className="cd-error-icon">⚠️</span>
          <h3>Something went wrong</h3>
          <p>{error || "Customer not found."}</p>
        </div>
      </div>
    );
  }

  const displayName = customer.fullName || customer.name || "Unknown";

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="cd-container">

      {/* ── Back Button ── */}
      <button className="cd-back-btn" onClick={() => navigate("/admin/customers")}>
        <ArrowLeft size={15} />
        Back to Customers
      </button>

      {/* ══════════════════════════════════════
          HERO CARD
      ══════════════════════════════════════ */}
      <div className="cd-hero">
        <div className="cd-hero-avatar" style={{ background: avatarColor }}>
          <span className="cd-avatar-ring" />
          {getInitials(displayName)}
        </div>

        <div className="cd-hero-info">
          <h1 className="cd-hero-name">{displayName}</h1>
          <p className="cd-hero-email">
            <Mail size={14} />
            {customer.email || "—"}
          </p>
          <div className="cd-hero-meta">
            <span className={`cd-hero-badge ${customer.status || "active"}`}>
              <span className="cd-hero-dot" />
              {customer.status || "active"}
            </span>
            <span className="cd-hero-joined">
              <Calendar size={13} />
              Joined {formatDate(customer.created_at)}
            </span>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          STATS ROW
      ══════════════════════════════════════ */}
      <div className="cd-stats-row">
        <div className="cd-stat-card orange">
          <div className="cd-stat-top">
            <span className="cd-stat-label">Total Orders</span>
            <div className="cd-stat-icon-wrap orange">
              <ShoppingBag size={17} />
            </div>
          </div>
          <span className="cd-stat-value">{stats.totalOrders}</span>
          <span className="cd-stat-sub">All time orders</span>
        </div>

        <div className="cd-stat-card green">
          <div className="cd-stat-top">
            <span className="cd-stat-label">Total Spent</span>
            <div className="cd-stat-icon-wrap green">
              <DollarSign size={17} />
            </div>
          </div>
          <span className="cd-stat-value">{formatCurrency(stats.totalSpent)}</span>
          <span className="cd-stat-sub">Lifetime value</span>
        </div>

        <div className="cd-stat-card orange">
          <div className="cd-stat-top">
            <span className="cd-stat-label">Avg. Order</span>
            <div className="cd-stat-icon-wrap orange">
              <TrendingUp size={17} />
            </div>
          </div>
          <span className="cd-stat-value">{formatCurrency(stats.averageOrder)}</span>
          <span className="cd-stat-sub">Per order</span>
        </div>

        <div className="cd-stat-card green">
          <div className="cd-stat-top">
            <span className="cd-stat-label">Delivered</span>
            <div className="cd-stat-icon-wrap green">
              <Package size={17} />
            </div>
          </div>
          <span className="cd-stat-value">{stats.delivered}</span>
          <span className="cd-stat-sub">Completed orders</span>
        </div>
      </div>

      {/* ══════════════════════════════════════
          MAIN GRID
      ══════════════════════════════════════ */}
      <div className="cd-grid">

        {/* ── LEFT COLUMN ── */}
        <div className="cd-left">

          {/* Personal Info Card */}
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-header-icon">
                <User size={15} />
              </div>
              <span className="cd-card-title">Personal Information</span>
            </div>
            <div className="cd-card-body">
              <div className="cd-info-list">

                <div className="cd-info-item">
                  <div className="cd-info-icon"><User size={15} /></div>
                  <div className="cd-info-content">
                    <span className="cd-info-label">Full Name</span>
                    <span className="cd-info-value">{displayName}</span>
                  </div>
                </div>

                <div className="cd-divider" />

                <div className="cd-info-item">
                  <div className="cd-info-icon"><Mail size={15} /></div>
                  <div className="cd-info-content">
                    <span className="cd-info-label">Email Address</span>
                    <span className="cd-info-value">{customer.email || "—"}</span>
                  </div>
                </div>

                <div className="cd-divider" />

                <div className="cd-info-item">
                  <div className="cd-info-icon"><Phone size={15} /></div>
                  <div className="cd-info-content">
                    <span className="cd-info-label">Phone Number</span>
                    <span className="cd-info-value">{customer.phone || "—"}</span>
                  </div>
                </div>

                <div className="cd-divider" />

                <div className="cd-info-item">
                  <div className="cd-info-icon"><MapPin size={15} /></div>
                  <div className="cd-info-content">
                    <span className="cd-info-label">Address</span>
                    <span className="cd-info-value">{customer.address || "—"}</span>
                  </div>
                </div>

                <div className="cd-divider" />

                <div className="cd-info-item">
                  <div className="cd-info-icon"><Hash size={15} /></div>
                  <div className="cd-info-content">
                    <span className="cd-info-label">Customer ID</span>
                    <span className="cd-info-value" style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                      {customer._id || customer.id || "—"}
                    </span>
                  </div>
                </div>

                <div className="cd-divider" />

                <div className="cd-info-item">
                  <div className="cd-info-icon"><Calendar size={15} /></div>
                  <div className="cd-info-content">
                    <span className="cd-info-label">Member Since</span>
                    <span className="cd-info-value">{formatDate(customer.created_at)}</span>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Activity Timeline Card */}
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-header-icon">
                <Clock size={15} />
              </div>
              <span className="cd-card-title">Recent Activity</span>
            </div>
            <div className="cd-notes-body">
              {orders.slice(0, 4).length > 0 ? (
                orders.slice(0, 4).map((o, i) => (
                  <div key={o._id || i} className="cd-note-item">
                    <div className="cd-note-dot-wrap">
                      <div className={`cd-note-dot ${i % 2 === 0 ? "" : "green"}`} />
                      {i < 3 && <div className="cd-note-line" />}
                    </div>
                    <div className="cd-note-content">
                      <div className="cd-note-text">
                        Placed order{" "}
                        <span style={{ color: "#d67e43", fontWeight: 700 }}>
                          #{String(o._id || o.id || "").slice(-6).toUpperCase()}
                        </span>
                        {" "}— {formatCurrency(o.total_price)}
                      </div>
                      <div className="cd-note-time">
                        {formatDate(o.created_at)} · <OrderBadge status={o.status} />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: "center", padding: "1.5rem 0", color: "#9ca3af", fontSize: "0.86rem" }}>
                  No activity yet
                </div>
              )}
            </div>
          </div>

        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="cd-right">

          {/* Orders Table */}
          <div className="cd-orders-card">
            <div className="cd-orders-header">
              <div className="cd-orders-header-left">
                <div className="cd-card-header-icon" style={{ background: "rgba(214,126,67,0.15)", color: "#d67e43", width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ShoppingBag size={15} />
                </div>
                <span className="cd-card-title">Order History</span>
                <span className="cd-orders-count">{stats.totalOrders} orders</span>
              </div>
            </div>

            <div className="cd-orders-table-wrap">
              {orders.length === 0 ? (
                <div className="cd-orders-empty">
                  <span className="cd-orders-empty-icon">📦</span>
                  <h4>No orders yet</h4>
                  <p>This customer hasn't placed any orders</p>
                </div>
              ) : (
                <table className="cd-orders-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Date</th>
                      <th>Items</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o, i) => (
                      <tr key={o._id || o.id || i}>
                        <td>
                          <span className="cd-order-id">
                            #{String(o._id || o.id || "").slice(-6).toUpperCase()}
                          </span>
                        </td>
                        <td>{formatDate(o.created_at)}</td>
                        <td>{o.adcart?.length || 0}</td>
                        <td>
                          <span className="cd-order-amount">
                            {formatCurrency(o.total_price)}
                          </span>
                        </td>
                        <td>
                          <OrderBadge status={o.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default CustomerDetail;